import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ReportExporter from '../components/ReportExporter';
import {
  FileText,
  TrendingUp,
  IndianRupee,
  Clock,
  CheckCircle2,
  ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "brand-navy" }) => (
  <Card className="relative overflow-hidden bg-white border-0 shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-brand-navy font-heading">{value}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 text-${color}`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-3 text-xs text-emerald-600">
          <ArrowUpRight className="h-3 w-3" />
          <span>{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const StageChip = ({ stage, count }) => {
  const stageColors = {
    'Application': 'bg-blue-100 text-blue-700',
    'Document Verification': 'bg-amber-100 text-amber-700',
    'Bank Submission': 'bg-purple-100 text-purple-700',
    'Sanctioned': 'bg-emerald-100 text-emerald-700',
    'Disbursed': 'bg-green-100 text-green-700',
    'Closed': 'bg-slate-100 text-slate-600'
  };

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-600">{stage}</span>
      <Badge className={`${stageColors[stage] || 'bg-slate-100 text-slate-600'} font-semibold`}>
        {count}
      </Badge>
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('month');

  const today = new Date();
  const currentMonthIdx = today.getMonth(); // 0-11
  const currentQuarter = Math.floor(currentMonthIdx / 3) + 1;
  const currentYear = today.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthIdx + 1); // 1-12
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter); // 1-4
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    fetchStats();
  }, [periodFilter, selectedMonth, selectedQuarter, selectedYear]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({ period: periodFilter });
      if (periodFilter === 'month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (periodFilter === 'quarter') {
        params.append('quarter', selectedQuarter);
        params.append('year', selectedYear);
      } else if (periodFilter === 'year') {
        params.append('year', selectedYear);
      }
      const response = await axios.get(`${API}/dashboard/stats?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const stages = ['Application', 'Document Verification', 'Bank Submission', 'Sanctioned', 'Disbursed', 'Closed'];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome to Atharva Finance Solutions</p>
        </div>
        <ReportExporter type="dashboard" />
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'all', label: 'All Time' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`dashboard-period-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}

        {/* Month picker */}
        {periodFilter === 'month' && (
          <>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-9" data-testid="dashboard-select-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((name, idx) => (
                  <SelectItem key={idx + 1} value={(idx + 1).toString()}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9" data-testid="dashboard-month-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}

        {/* Quarter picker */}
        {periodFilter === 'quarter' && (
          <>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[170px] h-9" data-testid="dashboard-select-quarter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9" data-testid="dashboard-quarter-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}

        {/* Year picker */}
        {periodFilter === 'year' && (
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9" data-testid="dashboard-select-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Loan Files"
          value={stats?.total_loans || 0}
          subtitle="All time applications"
          icon={FileText}
          color="brand-navy"
        />
        <StatCard
          title="Active Loans"
          value={stats?.active_loans || 0}
          subtitle="In progress"
          icon={Clock}
          color="amber-500"
        />
        <StatCard
          title="Disbursed"
          value={stats?.disbursed_loans || 0}
          subtitle="Successfully completed"
          icon={CheckCircle2}
          color="emerald-500"
        />
        <StatCard
          title="Total Amount"
          value={formatCurrency(stats?.total_loan_amount)}
          subtitle="Loan portfolio"
          icon={IndianRupee}
          color="brand-gold"
        />
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Loans by Stage */}
        <Card className="border-0 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy">Loans by Stage</CardTitle>
            <CardDescription>Current distribution of loan files</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {stages.map((stage) => (
                <StageChip
                  key={stage}
                  stage={stage}
                  count={stats?.loans_by_stage?.[stage] || 0}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Loans by Type */}
        <Card className="border-0 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy">Loans by Type</CardTitle>
            <CardDescription>Distribution across loan categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.loans_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{type}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-navy rounded-full"
                        style={{ width: `${Math.min((count / (stats?.total_loans || 1)) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-brand-navy w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
              {Object.keys(stats?.loans_by_type || {}).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No loan data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
